/**
 * SaiposCard — Card de integração ERP SAIPOS no marketplace.
 * v0.99.18 · Fev/2026
 *
 * Estados visuais:
 *  - disconnected: mostra botão "Conectar" com CTA claro pro token SAIPOS
 *  - connected: mostra badge verde + info de sync + botões Ressincronizar/Desconectar
 *  - error: banner vermelho com mensagem + botão pra reconectar
 *
 * O cliente cola o token (obtido em https://forms.gle/ayJKuNQ7EGUMxFSLA)
 * e a integração fica ativa. Todo trabalho pesado (sync agendada, dedup,
 * enriquecimento de contexto) fica no backend.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SAIPOS_TOKEN_URL = "https://forms.gle/ayJKuNQ7EGUMxFSLA";
const SAIPOS_DOCS_URL = "https://saipos-data-api.readme.io/";

export default function SaiposCard() {
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({ api_token: "", sync_interval_hours: 6 });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/saipos");
      setIntegration(data);
    } catch (e) {
      // Se ainda não existir, backend retorna default disconnected
      setIntegration({ provider: "saipos", status: "disconnected", config: {} });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const testAndConnect = async () => {
    if (!form.api_token || form.api_token.length < 10) {
      toast.error("Cole o token SAIPOS antes de conectar.");
      return;
    }
    setConnecting(true);
    try {
      // Teste rápido primeiro pra dar feedback antes de persistir
      const testResp = await api.post("/integrations/saipos/test", {
        api_token: form.api_token,
      });
      toast.success(
        `Token válido! Lojas detectadas: ${(testResp.data.detected_stores || []).join(", ") || "nenhuma nas últimas 24h"}`,
      );
      // Se OK, persiste
      const { data } = await api.post("/integrations/saipos/connect", {
        api_token: form.api_token,
        sync_interval_hours: form.sync_interval_hours,
      });
      setIntegration(data);
      setExpanded(false);
      setForm({ api_token: "", sync_interval_hours: 6 });
      toast.success("SAIPOS conectada! Sincronização iniciada.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Erro desconhecido";
      toast.error(`Falha ao conectar: ${msg}`);
    } finally {
      setConnecting(false);
    }
  };

  const resync = async () => {
    setResyncing(true);
    try {
      const { data } = await api.post("/integrations/saipos/resync");
      if (data.ok) {
        toast.success(
          `Sincronizado! ${data.sales_upserted || 0} pedidos atualizados.`,
        );
      } else {
        toast.error(`Sync falhou: ${data.reason} ${data.detail || ""}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao ressincronizar");
    } finally {
      setResyncing(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Desconectar SAIPOS? O histórico já sincronizado será preservado.")) return;
    try {
      await api.delete("/integrations/saipos");
      toast.success("SAIPOS desconectada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao desconectar");
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5 text-sm text-slate-500">
        Carregando integração SAIPOS…
      </div>
    );
  }

  const isConnected = integration?.status === "connected";
  const hasError = integration?.status === "error";
  const cfg = integration?.config || {};

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="saipos-card">
      {/* Header */}
      <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-700 font-bold text-sm">
            SP
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-[15px] font-semibold text-slate-900">SAIPOS</h3>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase tracking-wide">ERP</span>
              {isConnected && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800" data-testid="saipos-status-connected">
                  Conectado
                </span>
              )}
              {hasError && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800" data-testid="saipos-status-error">
                  Erro
                </span>
              )}
              {!isConnected && !hasError && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-testid="saipos-status-disconnected">
                  Não conectado
                </span>
              )}
            </div>
            <p className="text-[12.5px] text-slate-500 mt-0.5">
              ERP de restaurantes / food service. Sincroniza pedidos, itens, financeiro e estoque.
            </p>
          </div>
        </div>
      </div>

      {/* Connected state */}
      {isConnected && (
        <div className="p-5 space-y-3 text-[13px] text-slate-700">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Token</div>
              <div className="font-mono text-[12px] text-slate-700">{cfg.api_token || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Lojas autorizadas</div>
              <div>{(cfg.id_stores || []).join(", ") || "auto"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Intervalo de sync</div>
              <div>{cfg.sync_interval_hours || 6}h</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Última sync</div>
              <div>{cfg.last_synced_at ? new Date(cfg.last_synced_at).toLocaleString("pt-BR") : "aguardando"}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={resync}
              disabled={resyncing}
              data-testid="saipos-resync-btn"
              className="text-[13px] px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {resyncing ? "Sincronizando…" : "Ressincronizar agora"}
            </button>
            <button
              type="button"
              onClick={disconnect}
              data-testid="saipos-disconnect-btn"
              className="text-[13px] text-rose-600 hover:underline"
            >
              Desconectar
            </button>
          </div>
        </div>
      )}

      {/* Error state */}
      {hasError && cfg.last_error && (
        <div className="p-4 bg-rose-50 border-b border-rose-100 text-[12.5px] text-rose-800">
          <strong>Erro na última sync:</strong> {cfg.last_error}
        </div>
      )}

      {/* Disconnected / connect form */}
      {(!isConnected || hasError) && (
        <div className="p-5 space-y-3">
          {!expanded ? (
            <div className="flex items-center justify-between gap-3">
              <div className="text-[12.5px] text-slate-500 leading-relaxed">
                Cole seu token SAIPOS pra ativar. Ainda não tem o token?
                <a href={SAIPOS_TOKEN_URL} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline ml-1">
                  Solicite aqui à SAIPOS
                </a>{" "}
                (SLA ~1 dia útil, R$ 59,90/mês por loja).
              </div>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                data-testid="saipos-connect-btn"
                className="text-[13px] px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap"
              >
                {hasError ? "Reconectar" : "Conectar"}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-[12px] font-medium text-slate-700 mb-1">
                  Token da API SAIPOS (JWT)
                </label>
                <textarea
                  value={form.api_token}
                  onChange={(e) => setForm({ ...form, api_token: e.target.value })}
                  data-testid="saipos-token-input"
                  placeholder="Cole aqui o token JWT que você recebeu por e-mail da SAIPOS…"
                  className="w-full text-[12px] font-mono border border-slate-300 rounded px-3 py-2 h-24 resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Documentação:{" "}
                  <a href={SAIPOS_DOCS_URL} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">
                    saipos-data-api.readme.io
                  </a>
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-medium text-slate-700 mb-1">
                  Intervalo de sincronização
                </label>
                <select
                  value={form.sync_interval_hours}
                  onChange={(e) => setForm({ ...form, sync_interval_hours: Number(e.target.value) })}
                  data-testid="saipos-interval-select"
                  className="text-[13px] border border-slate-300 rounded px-3 py-1.5"
                >
                  <option value={1}>A cada 1 hora</option>
                  <option value={3}>A cada 3 horas</option>
                  <option value={6}>A cada 6 horas (recomendado)</option>
                  <option value={12}>A cada 12 horas</option>
                  <option value={24}>1× por dia</option>
                </select>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={testAndConnect}
                  disabled={connecting}
                  data-testid="saipos-submit-btn"
                  className="text-[13px] px-4 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {connecting ? "Validando token…" : "Testar e conectar"}
                </button>
                <button
                  type="button"
                  onClick={() => { setExpanded(false); setForm({ api_token: "", sync_interval_hours: 6 }); }}
                  className="text-[13px] text-slate-500 hover:underline"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
